import { Pencil, RotateCcw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { FLAG, hasFlag, type PostOperationReportListItem } from '../caseTypes';
import { postOperationReportsApi } from '../postOperationReportsApi';
import { failedFilesMessage } from '../utils/fileAll';
import { PostOperationReportModal } from './PostOperationReportModal';

/**
 * A case's Post Operation Reports — the case profile's tab. The anchor OPENS a report on its own
 * page, where its summary is read and its files are kept.
 *
 * Every control carries the report KEY of its act and is DISABLED, with the reason, by the CASE's
 * flag: Upload writes a report, Edit corrects one, Delete removes or restores one. A deleted case
 * takes no new report.
 */
export function CaseReportsPanel({
  caseId,
  caseIsActive,
  flags,
}: {
  caseId: string;
  caseIsActive: boolean;
  /** The caller's flags on the case — `FLAG`. */
  flags: number;
}) {
  const navigate = useNavigate();
  const toast = useToast();

  const [showDeleted, setShowDeleted] = useState(false);
  const [modal, setModal] = useState<{ editing: PostOperationReportListItem | null } | null>(null);
  const [confirming, setConfirming] = useState<{ kind: 'delete' | 'restore'; row: PostOperationReportListItem } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const fetchPage = useCallback(
    (query: DataTableQuery, signal: AbortSignal) =>
      postOperationReportsApi.list(caseId, { ...query, status: showDeleted ? undefined : 'active' }, signal),
    [caseId, showDeleted],
  );

  const state = useDataTable<PostOperationReportListItem>({ fetchPage, pageSize: 25 });

  const caseReason = caseIsActive ? null : 'This case is deleted.';
  const createReason = caseReason ?? (hasFlag(flags, FLAG.upload) ? null : 'You have not been given Upload on this case.');
  const editReason = caseReason ?? (hasFlag(flags, FLAG.edit) ? null : 'You have not been given Edit on this case.');
  const deleteReason = caseReason ?? (hasFlag(flags, FLAG.delete) ? null : 'You have not been given Delete on this case.');

  const rowActions = useCallback(
    (row: PostOperationReportListItem): MenuItem[] => [
      // In the menu rather than `onEdit`, so it can be disabled by the CASE's Edit flag with the reason.
      {
        key: `edit-${row.id}`,
        label: 'Edit',
        icon: <Pencil className="h-4 w-4" aria-hidden="true" />,
        permission: 'post-operation-reports.update',
        disabledReason: row.isActive ? editReason : 'This report is deleted. Restore it first.',
        onClick: () => setModal({ editing: row }),
      },
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'post-operation-reports.delete',
        disabledReason: row.isActive ? 'This report is not deleted.' : deleteReason,
        onClick: () => setConfirming({ kind: 'restore', row }),
      },
    ],
    [deleteReason, editReason],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') {
        await postOperationReportsApi.deactivate(confirming.row.id);
        toast.success(`${confirming.row.title} was deleted.`);
      } else {
        await postOperationReportsApi.activate(confirming.row.id);
        toast.success(`${confirming.row.title} was restored.`);
      }

      setConfirming(null);
      state.reload();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <DataTablePanel
        id="case-reports"
        fill
        state={state}
        columns={COLUMNS}
        rowKey={(row) => row.id}
        searchPlaceholder="Search title or summary"
        onCreate={() => setModal({ editing: null })}
        createLabel="New report"
        createPermission="post-operation-reports.create"
        createDisabledReason={createReason}
        settingsItems={[
          {
            key: 'show-deleted',
            label: 'Show deleted',
            section: 'Records',
            checked: showDeleted,
            onClick: () => setShowDeleted((v) => !v),
          },
        ]}
        onRowOpen={(row) => navigate(`/cases/${caseId}/reports/${row.id}`)}
        openPermission="post-operation-reports.read"
        rowActions={rowActions}
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission="post-operation-reports.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) => (!row.isActive ? 'This report is already deleted.' : deleteReason)}
        otherInfo={(row) => row}
        emptyMessage="No Post Operation Report has been written on this case yet."
        emptySearchMessage="No report matches that search."
      />

      {modal ? (
        <PostOperationReportModal
          caseId={caseId}
          editing={modal.editing}
          onClose={() => setModal(null)}
          onSaved={(saved, failed) => {
            const created = modal.editing === null;

            setModal(null);
            toast.success(created ? `${saved.title} was written.` : `${saved.title} was saved.`);
            if (failed.length > 0) toast.failure(failedFilesMessage(failed));

            if (created) navigate(`/cases/${caseId}/reports/${saved.id}`);
            else state.reload();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={confirming.kind === 'delete' ? 'Delete report' : 'Restore report'}
          danger={confirming.kind === 'delete'}
          requireConfirm
          loading={running}
          id={`case-report-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              <p>
                {confirming.kind === 'delete'
                  ? `${confirming.row.title} stops appearing on this case. Nothing is erased — its files are kept, and it can be restored.`
                  : `${confirming.row.title} appears on this case again, with its files.`}
              </p>
              {error ? (
                <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const COLUMNS: DataColumn<PostOperationReportListItem>[] = [
  { key: 'title', header: 'Title', width: 300, sortable: true, render: (row) => <TextCell value={row.title} /> },
  { key: 'summary', header: 'Summary', width: 420, summary: true, render: (row) => <TextCell value={row.summary} /> },
  {
    key: 'attachmentCount',
    header: 'Files',
    width: 90,
    align: 'right',
    summary: true,
    render: (row) => <TextCell value={String(row.attachmentCount)} mono numeric />,
  },
  {
    key: 'status',
    header: 'Status',
    width: 110,
    status: true,
    render: (row) =>
      row.isActive ? <StatusText tone="active">Active</StatusText> : <StatusText tone="inactive">Deleted</StatusText>,
  },
];
